import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Network } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ApiError, apiFetch } from '@/api/client';
import {
  getRunFindingsSummary,
  listFindingsForRun,
} from '@/api/findings';
import { listRules } from '@/api/rules';
import {
  deleteAuditRun,
  downloadRunFindingsCsv,
  downloadRunNormalizedCsv,
  downloadRunReportCsv,
  downloadRunReportPdf,
  getRun,
} from '@/api/runs';
import { AuditTable } from '@/components/AuditTable';
import { FilingsTab } from '@/components/FilingsTab';
import { FindingListView } from '@/components/FindingListView';
import { FindingsReviewQueue } from '@/components/FindingsReviewQueue';
import { GlAccountsTab } from '@/components/GlAccountsTab';
import { OrphanItcTab } from '@/components/OrphanItcTab';
import { RunCustomerSlugContext } from '@/components/RunCustomerSlugContext';
import { Badge } from '@/components/ui/badge';
import { Breadcrumbs } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorCard } from '@/components/ui/error-card';
import { excludeAutoCleared } from '@/lib/autoCleared';
import { formatCurrency, formatDateTime } from '@/lib/format';
import { needsFullFindings } from '@/lib/needsFullFindings';
import { SuppliersTab } from '@/components/SuppliersTab';
import { cn } from '@/lib/utils';
import {
  summaryToRecoveryTotals,
  type OutcomeRecoveryTotals,
} from '@/lib/runRecoverySummary';
import { useClaimedLines } from '@/hooks/useClaimedLines';
import { useFindingsReview } from '@/hooks/useFindingsReview';
import { useTransactionDocuments } from '@/hooks/useTransactionDocuments';
import { useAuth } from '../auth/AuthContext';
import type {
  AuditRunOut,
  RuleOut,
} from '@/api/types';

interface MeResponse {
  id: string;
  user_type: 'practitioner' | 'customer';
}

export function RunDetailPage() {
  const { id } = useParams({ from: '/runs/$id' });
  const { isSignedIn, userKey, getIdToken } = useAuth();

  const me = useQuery({
    queryKey: ['me', userKey],
    enabled: isSignedIn,
    queryFn: async () => apiFetch<MeResponse>('/api/me', { idToken: (await getIdToken() ?? '') }),
  });

  const run = useQuery({
    queryKey: ['runs', id, userKey],
    enabled: isSignedIn,
    queryFn: async () => getRun(id, (await getIdToken() ?? '')),
    // Poll while the background task is still working so the status flips
    // from "running" -> "completed" / "failed" without a manual refresh.
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2000 : false),
  });

  // Run-header cards read the server-computed summary (recovery +
  // compliance, per status, aggregated in SQL) instead of summing the
  // loaded findings client-side. The client sum silently under-counted
  // whenever the findings list was paginated / partially loaded — a
  // full-population run could show $0 recoverable while the DB held
  // hundreds of thousands of dollars pending. This query is tiny and
  // independent of the (large) findings load.
  const findingsSummary = useQuery({
    queryKey: ['findings', 'summary', id, userKey],
    enabled: isSignedIn && run.data !== undefined && run.data.status !== 'running',
    queryFn: async () => getRunFindingsSummary(id, (await getIdToken()) ?? ''),
  });

  // Rule catalog drives the per-finding detail panel. Fetched in parallel
  // with findings; the detail panel renders a graceful fallback if the
  // catalog hasn't arrived yet or the finding's rule isn't on disk.
  const rules = useQuery({
    queryKey: ['rules', userKey],
    enabled: isSignedIn,
    queryFn: async () => listRules((await getIdToken() ?? '')),
  });

  useEffect(() => {
    if (rules.isError) {
      console.warn('Rule catalog query failed', rules.error);
    }
  }, [rules.isError, rules.error]);

  if (!isSignedIn) {
    return (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Not signed in</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (run.isPending) return <p className="text-brand-slate">Loading…</p>;
  // Only hard-fail when there's no cached run. A background refetch that
  // 401s during a Clerk session refresh must not swap the whole page to
  // "Run not found" — keep the last-good data on screen (INP-502).
  if (run.isError && !run.data) return <ErrorCard title="Run not found" error={run.error} />;
  if (!run.data) return <p className="text-brand-slate">Loading…</p>;

  const r = run.data;
  const canReview = me.data?.user_type === 'practitioner';
  // Per-status recovery breakdown, computed from the loaded findings so each
  // category (Recoverable / Compliance) shows where its total sits across the
  // disposition statuses. Null until findings arrive — the header falls back
  // to the run-level Total + Approved the API carries.
  // Hide the auto-cleared coverage rows from every findings-derived surface
  // (funnel, queue, list, audit, rollups) unless the practitioner opts in.
  // Sourced from the server summary (not the loaded rows): the default
  // reviewable-only load doesn't fetch the coverage rows, so counting them
  // client-side would read 0 and hide the "show auto-cleared" toggle.
  const recoverySummary = findingsSummary.data
    ? summaryToRecoveryTotals(findingsSummary.data)
    : null;

  return (
    <RunCustomerSlugContext.Provider value={r.customer_slug ?? null}>
    <div className="grid gap-6">
      <header>
        <Breadcrumbs>
          <Link to="/customers" className="text-brand-royal underline-offset-4 hover:underline">
            Customers
          </Link>
          {/* Parent links only render for engagement-linked runs; CLI-triggered
              runs have no account/engagement context (the API ships null for
              those breadcrumb fields). */}
          {r.customer_slug && r.customer_name && (
            <Link
              to="/customers/$slug"
              params={{ slug: r.customer_slug }}
              className="text-brand-royal underline-offset-4 hover:underline"
            >
              {r.customer_name}
            </Link>
          )}
          {r.engagement_id && r.engagement_name && (
            <Link
              to="/engagements/$id"
              params={{ id: r.engagement_id }}
              className="text-brand-royal underline-offset-4 hover:underline"
            >
              {r.engagement_name}
            </Link>
          )}
          <span className="text-brand-navy">Recovery</span>
        </Breadcrumbs>
        <div className="mt-2 flex items-baseline justify-between gap-3">
          <div className="flex items-baseline gap-2">
            <h2 className="flex items-baseline gap-2 text-2xl font-semibold tracking-tight text-brand-navy">
              {r.name}
              <span className="font-mono text-sm font-normal text-brand-slate">
                {r.rule_set_id}@v{r.rule_set_version}
              </span>
            </h2>
            <StatusBadge status={r.status} pendingFindings={r.pending_findings} />
          </div>
          <div className="flex items-center gap-3">
            {r.status !== 'running' && (
              <Link
                to="/runs/$id/network"
                params={{ id: r.id }}
                className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-brand-royal shadow-xs hover:bg-muted/50 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
                title="Open the customer–supplier transaction network"
              >
                <Network className="h-4 w-4" aria-hidden="true" />
                Supplier network
              </Link>
            )}
            {r.status === 'completed' && <DownloadPdfReportButton runId={r.id} />}
            {r.status === 'completed' && <DownloadRecoveryCsvButton runId={r.id} />}
            {r.status !== 'running' && <DownloadFindingsButton runId={r.id} />}
            {r.status !== 'running' && <DownloadNormalizedButton runId={r.id} />}
            {canReview && <DeleteRunButton run={r} />}
          </div>
        </div>
        <p className="mt-1 text-sm text-brand-slate">
          Started {formatDateTime(r.started_at)}
          {r.completed_at && ` · finished ${formatDateTime(r.completed_at)}`}
        </p>
        {/*
          Recovery and compliance-risk are kept in two visually distinct
          funnel blocks so the customer-claimable dollars (recovery) are
          never conflated with the exposure dollars (compliance risk).
          Each block sums its own category and breaks the total down by
          disposition status (Approved / Needs review / Pending / Rejected)
          so the practitioner sees where the run stands at a glance. The
          four status rows sum exactly to Total. Computed client-side from
          the findings list (override-aware) — mirrors the server's naive
          cross-currency SUM, so the Total row matches the run header.
          While findings load we fall back to the run-level Total + Approved
          the API already carries.
        */}
        <div className="mt-3 flex flex-wrap items-start gap-x-10 gap-y-3 text-sm">
          <RecoveryFunnel
            title="Recoverable"
            tone="emerald"
            rows={
              recoverySummary
                ? outcomeFunnelRows(recoverySummary.recovery)
                : [
                    { label: 'Total', value: r.total_recovery },
                    { label: 'Approved', value: r.approved_recovery },
                  ]
            }
          />
          <RecoveryFunnel
            title="Compliance"
            tone="amber"
            rows={
              recoverySummary
                ? outcomeFunnelRows(recoverySummary.compliance)
                : [
                    { label: 'Total', value: r.total_compliance_risk },
                    { label: 'Approved', value: r.approved_compliance_risk },
                  ]
            }
          />
          <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1 text-xs text-brand-slate">
            <dt>transactions</dt>
            <dd className="text-right font-mono text-brand-navy">
              {r.transaction_count.toLocaleString()}
            </dd>
            <dt>findings</dt>
            <dd className="text-right font-mono text-brand-navy">
              {r.finding_count.toLocaleString()}
            </dd>
          </dl>
        </div>
      </header>

      <section className="grid gap-3">
        {r.status === 'running' ? (
          <Card className="max-w-xl">
            <CardHeader>
              <CardTitle>Run in progress…</CardTitle>
              <CardDescription>
                Findings will appear here when the run completes. This page is polling
                every two seconds.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : r.status === 'failed' ? (
          <Card className="max-w-xl border-destructive/30">
            <CardHeader>
              <CardTitle>Run failed</CardTitle>
              <CardDescription>
                The audit-run task aborted before producing findings. Check CloudWatch
                logs for the underlying error.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          // The full-finding load is now LAZY and lives inside FindingsTabs
          // (it fires only when a tab / filter / sort needs the whole set;
          // the default Queue + List render off server pages). The tab bar
          // is therefore reachable immediately — including the self-fetching
          // Filings + Orphan ITC tabs, which previously sat behind the
          // findings-query gate (INP-700).
          <FindingsTabs
            rules={rules.data?.items ?? []}
            rulesLoading={rules.isPending}
            rulesError={rules.isError}
            canReview={canReview}
            runId={id}
          />
        )}
      </section>
    </div>
    </RunCustomerSlugContext.Provider>
  );
}

// ── Tabs wrapper + audit log ─────────────────────────────────────────


type TabKey =
  | 'queue'
  | 'list'
  | 'audit'
  | 'suppliers'
  | 'glAccounts'
  | 'filings'
  | 'orphanItc';

function isTabKey(v: unknown): v is TabKey {
  return (
    v === 'queue' ||
    v === 'list' ||
    v === 'audit' ||
    v === 'suppliers' ||
    v === 'glAccounts' ||
    v === 'filings' ||
    v === 'orphanItc'
  );
}


function FindingsTabs({
  rules,
  rulesLoading,
  rulesError,
  canReview,
  runId,
}: {
  rules: RuleOut[];
  rulesLoading: boolean;
  rulesError: boolean;
  canReview: boolean;
  runId: string;
}) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  // The active tab lives in the URL (`?tab=list`) so the List view is
  // bookmarkable and survives a reload. `strict: false` reads the loose
  // search param without a route-level schema (mirrors customers.$slug).
  const search = useSearch({ strict: false }) as { tab?: string };
  const tabNavigate = useNavigate();
  const tab: TabKey = isTabKey(search.tab) ? search.tab : 'queue';
  const setTab = (next: TabKey) => {
    void tabNavigate({
      to: '/runs/$id',
      params: { id: runId },
      search: (prev) => ({ ...prev, tab: next }),
      replace: true,
    });
  };

  // Auto-cleared coverage rows (no_issue_detected) are hidden from every
  // review surface by default; the practitioner can opt in to see the full
  // transaction population. INP-684 slice 2.
  const [showAutoCleared, setShowAutoCleared] = useState(false);

  // Server-computed summary drives the tab badges + the auto-cleared
  // toggle count (honest under pagination — independent of how many
  // findings are loaded). Small, findings-load-independent query.
  const findingsSummary = useQuery({
    queryKey: ['findings', 'summary', runId, userKey],
    enabled: isSignedIn,
    queryFn: async () => getRunFindingsSummary(runId, (await getIdToken()) ?? ''),
  });

  // The full-finding load decision. `needsFullFindings` = a tab that
  // renders the whole set (audit / suppliers / glAccounts), OR a
  // client-only filter is active, OR the List has a column sort, OR the
  // auto-cleared coverage rows are shown. Mirrored from the review hook
  // below via state (the hook owns the filter + sort signals); the query's
  // `enabled` reads this so the default Queue + List never trigger the
  // 54k-row walk. INP-789/INP-778.
  const [loadDecision, setLoadDecision] = useState({
    hasClientOnlyFilter: false,
    listSortActive: false,
  });
  const needsFull = needsFullFindings({
    tab,
    hasClientOnlyFilter: loadDecision.hasClientOnlyFilter,
    listSortActive: loadDecision.listSortActive,
    showAutoCleared,
  });
  const reviewableOnly = !showAutoCleared;
  const findingsQuery = useQuery({
    queryKey: ['findings', 'by-run', runId, userKey, reviewableOnly],
    enabled: isSignedIn && needsFull,
    queryFn: async () =>
      listFindingsForRun(runId, (await getIdToken()) ?? '', { reviewableOnly }),
  });
  // The full finding set, auto-cleared filtered per the toggle. Empty when
  // the load isn't needed — the default Queue + List then run purely off
  // the shared server-paged queue.
  const findings = useMemo(() => {
    if (!findingsQuery.data) return [];
    return showAutoCleared
      ? findingsQuery.data.items
      : excludeAutoCleared(findingsQuery.data.items);
  }, [findingsQuery.data, showAutoCleared]);

  const autoClearedCount = findingsSummary.data?.auto_cleared ?? 0;
  const autoClearedCountKnown = findingsSummary.data !== undefined;
  const showAutoClearedToggle = autoClearedCount > 0 || !autoClearedCountKnown;

  // One batched invoice-link lookup for the whole run, shared by every
  // tab — links resolve via (customer_id, transaction_id) so an invoice
  // assigned on any run surfaces here too.
  const customerSlug = useContext(RunCustomerSlugContext);

  // Invoice-link + claimed-line data is fetched per transaction, chunked.
  // Fetching it for every reviewable finding up front fired ~N/chunk
  // requests (two storms — documents + claims) that saturated the browser
  // and stalled the page (INP-789). The queue shows one card at a time
  // (reads links for `current.transaction_id`) and the attached-count
  // comes from the server summary, so only the transactions on screen
  // need links. Scope the fetch to the queue's loaded window and feed the
  // result back into the review hook — which needs the full set only for
  // the client-only invoice sub-filters (handled by `needsAllLinks`).
  const [reviewLinks, setReviewLinks] = useState<
    Pick<
      ReturnType<typeof useTransactionDocuments>,
      | 'linksByTransaction'
      | 'extractionsByDocument'
      | 'markedTransactions'
      | 'linksReady'
      | 'linksError'
    >
  >(() => ({
    linksByTransaction: new Map(),
    extractionsByDocument: new Map(),
    markedTransactions: new Set(),
    linksReady: false,
    linksError: false,
  }));

  // One shared review-state instance for the whole findings surface so the
  // flashcard queue and the list view read the same filters + actions.
  // Living here (a tab never unmounts its parent) means filter state also
  // persists across tab switches instead of resetting.
  const review = useFindingsReview({
    findings,
    rules,
    runId,
    ...reviewLinks,
  });

  // Sync the full-load decision from the review hook (which owns the
  // filter + sort signals). `needsClientFullLoad` folds together
  // client-only filters, auto-cleared-visible, and list sort; the query
  // above ORs in the full-set tabs. State (not a ref) so a change
  // re-renders and the findings query re-evaluates `enabled`.
  useEffect(() => {
    setLoadDecision((prev) =>
      prev.hasClientOnlyFilter === review.needsClientFullLoad &&
      prev.listSortActive === review.listSortActive
        ? prev
        : {
            hasClientOnlyFilter: review.needsClientFullLoad,
            listSortActive: review.listSortActive,
          },
    );
  }, [review.needsClientFullLoad, review.listSortActive]);

  // Only fetch links/claims for the transactions actually on screen.
  //  - client-only invoice sub-filters (unattached / no_invoice /
  //    complete / incomplete) filter the whole set by attachment state,
  //    so they need every finding's links (`''` / `attached` push down);
  //  - the queue shows its loaded page window (`review.queueItems`);
  //  - the list tab reports its current page's ids (100 rows);
  //  - the audit tab (full coverage view) still needs the full set.
  const [listVisibleIds, setListVisibleIds] = useState<string[]>([]);
  const handleListVisibleIds = useCallback((ids: string[]) => setListVisibleIds(ids), []);
  const clientOnlyInvoiceFilter =
    review.invoiceFilter !== '' && review.invoiceFilter !== 'attached';
  const displayedTransactionIds = useMemo(() => {
    // Only the queue, list, and audit tabs render invoice/claims data.
    // suppliers / glAccounts / filings / orphanItc don't, so they fetch
    // nothing — never reintroduce the full-run burst by opening them.
    if (tab !== 'queue' && tab !== 'list' && tab !== 'audit') return [];
    // The client-only invoice sub-filters filter the queue/list client-
    // side by attachment state, so they need every finding's links.
    if (clientOnlyInvoiceFilter && (tab === 'queue' || tab === 'list')) {
      return findings.map((f) => f.transaction_id);
    }
    if (tab === 'queue') return review.queueItems.map((f) => f.transaction_id);
    if (tab === 'list') return listVisibleIds;
    return findings.map((f) => f.transaction_id); // audit — full coverage view
  }, [clientOnlyInvoiceFilter, tab, findings, review.queueItems, listVisibleIds]);
  const {
    linksByTransaction,
    extractionsByDocument,
    markedTransactions,
    linksReady,
    linksError,
  } = useTransactionDocuments(customerSlug, displayedTransactionIds);
  // The claimed-GST sibling lookup - same batching, same (scoped) id set.
  // For customers with no claim ledger loaded, `claimPeriodCount` stays 0
  // and no claimed-GST UI renders anywhere.
  const { claimsByTransaction, claimPeriodCount, claimsReady } = useClaimedLines(
    customerSlug,
    displayedTransactionIds,
  );

  // Feed the scoped links back into the review hook so its client-only
  // invoice filters + attachment fallbacks see them. One render behind;
  // converges immediately because the query key is stable once scoped
  // (the hook memoizes the maps on the query result).
  useEffect(() => {
    setReviewLinks({
      linksByTransaction,
      extractionsByDocument,
      markedTransactions,
      linksReady,
      linksError,
    });
  }, [linksByTransaction, extractionsByDocument, markedTransactions, linksReady, linksError]);
  // Tab badges read the server aggregates (honest under paging — the full
  // findings set may not be loaded). Falls back to loaded-row counts when
  // the summary / facets haven't landed.
  //  - queue: pending (untouched) + needs_review (flagged for follow-up),
  //    both still need a decision;
  //  - audit: approved + rejected (final decisions);
  //  - suppliers: distinct supplier count from the facets endpoint.
  const summary = findingsSummary.data;
  const loadedPendingCount = useMemo(
    () =>
      findings.filter((f) => f.status === 'pending' || f.status === 'needs_review')
        .length,
    [findings],
  );
  const pendingCount = summary
    ? summary.pending.count + summary.needs_review.count
    : loadedPendingCount;
  const reviewedCount = summary
    ? summary.approved.count + summary.rejected.count
    : findings.length - loadedPendingCount;
  const loadedSupplierCount = useMemo(() => {
    const set = new Set<string>();
    for (const f of findings) {
      const name = stringOrNull(f.evidence['supplier.name']);
      if (name) set.add(name);
    }
    return set.size;
  }, [findings]);
  // `review.suppliers` is facets-backed (falls back to loaded findings).
  const supplierCount = review.suppliers.length || loadedSupplierCount;

  // Whenever the full load is needed — a full-set tab (audit/suppliers/gl),
  // a client-only filter, a List sort, or the auto-cleared toggle — surface
  // its pending/error state rather than an empty panel. Gating on a full-set
  // tab alone let a filter/sort-triggered load fail silently into an empty
  // list.
  const fullLoadPending = needsFull && findingsQuery.isPending;
  const fullLoadFailed = needsFull && findingsQuery.isError && !findingsQuery.data;
  // The zero-findings card is only honest once we actually know the set is
  // empty: either the full load finished with no rows, or the server
  // summary reports a zero reviewable total.
  const summaryTotal = summary?.total;
  const knownEmpty =
    (findingsQuery.data !== undefined && findings.length === 0) ||
    (!needsFull && summaryTotal === 0);

  return (
    <div className="grid gap-4">
      {showAutoClearedToggle && (
        <label className="flex items-center gap-2 text-sm text-brand-slate">
          <input
            type="checkbox"
            checked={showAutoCleared}
            onChange={(ev) => setShowAutoCleared(ev.target.checked)}
          />
          {autoClearedCount > 0
            ? `Show ${autoClearedCount.toLocaleString()} auto-cleared transaction${
                autoClearedCount === 1 ? '' : 's'
              }`
            : 'Show auto-cleared transactions'}
          <span className="text-brand-slate/70">(reviewed, no recoverable issue)</span>
        </label>
      )}
      <div className="flex items-center gap-1 border-b border-border">
        <TabButton active={tab === 'queue'} onClick={() => setTab('queue')}>
          Review queue
          <Badge tone="muted" soft pill className="ml-2">
            {pendingCount} pending
          </Badge>
        </TabButton>
        <TabButton active={tab === 'list'} onClick={() => setTab('list')}>
          List view
        </TabButton>
        <TabButton active={tab === 'audit'} onClick={() => setTab('audit')}>
          Audit log
          <Badge tone="muted" soft pill className="ml-2">
            {reviewedCount} reviewed
          </Badge>
        </TabButton>
        <TabButton active={tab === 'suppliers'} onClick={() => setTab('suppliers')}>
          Suppliers
          <Badge tone="muted" soft pill className="ml-2">
            {supplierCount}
          </Badge>
        </TabButton>
        <TabButton active={tab === 'glAccounts'} onClick={() => setTab('glAccounts')}>
          GL accounts
        </TabButton>
        <TabButton active={tab === 'filings'} onClick={() => setTab('filings')}>
          Filings
        </TabButton>
        <TabButton active={tab === 'orphanItc'} onClick={() => setTab('orphanItc')}>
          Orphan ITC
        </TabButton>
      </div>

      {knownEmpty && tab !== 'filings' && tab !== 'orphanItc' ? (
        // Zero-finding run: every findings-derived panel is empty, so show
        // the single card instead. The Filings + Orphan ITC tabs still
        // render — they fetch their own data, so their empty/preview states
        // are meaningful on their own.
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>No findings</CardTitle>
            <CardDescription>This run produced zero matches.</CardDescription>
          </CardHeader>
        </Card>
      ) : fullLoadFailed ? (
        <ErrorCard title="Couldn't load findings" error={findingsQuery.error} />
      ) : fullLoadPending ? (
        <p className="text-brand-slate">Loading findings…</p>
      ) : (
        <>
      {tab === 'queue' && (
        <FindingsReviewQueue
          review={review}
          findings={findings}
          rulesLoading={rulesLoading}
          rulesError={rulesError}
          canReview={canReview}
          runId={runId}
          linksByTransaction={linksByTransaction}
          extractionsByDocument={extractionsByDocument}
          markedTransactions={markedTransactions}
          linksReady={linksReady}
          linksError={linksError}
          claimsByTransaction={claimsByTransaction}
          claimPeriodCount={claimPeriodCount}
          claimsReady={claimsReady}
        />
      )}
      {tab === 'list' && (
        // Keyed on runId so switching runs remounts the list — the sort +
        // last-visited hooks read from per-run storage on mount, so a shared
        // instance would carry one run's state onto another.
        <FindingListView
          key={runId}
          review={review}
          canReview={canReview}
          runId={runId}
          linksByTransaction={linksByTransaction}
          extractionsByDocument={extractionsByDocument}
          markedTransactions={markedTransactions}
          linksError={linksError}
          onVisibleTransactionIds={handleListVisibleIds}
        />
      )}
      {tab === 'audit' && (
        <AuditTable
          findings={findings}
          rules={rules}
          canReview={canReview}
          runId={runId}
          linksByTransaction={linksByTransaction}
          extractionsByDocument={extractionsByDocument}
          markedTransactions={markedTransactions}
        />
      )}
      {tab === 'suppliers' && (
        <SuppliersTab key={runId} findings={findings} rules={rules} runId={runId} />
      )}
      {tab === 'glAccounts' && <GlAccountsTab findings={findings} />}
      {tab === 'filings' && <FilingsTab runId={runId} canReview={canReview} />}
      {tab === 'orphanItc' && <OrphanItcTab runId={runId} />}
        </>
      )}
    </div>
  );
}


function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'border-b-2 border-brand-royal px-3 py-2 -mb-px text-sm font-medium text-brand-navy'
          : 'border-b-2 border-transparent px-3 py-2 -mb-px text-sm text-brand-slate transition-colors hover:text-brand-navy'
      }
    >
      {children}
    </button>
  );
}

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return value.length > 0 ? value : null;
}

/** A single header funnel block — Recovery (green) or Compliance risk
 *  (amber) — narrowing Total → Approved → Confirmed. Each block sums one
 *  category so the two dollar kinds are never mixed. */
// Funnel rows for one outcome category: Total on top (emphasized), then the
// four disposition statuses that sum to it.
function outcomeFunnelRows(
  totals: OutcomeRecoveryTotals,
): { label: string; value: number }[] {
  return [
    { label: 'Total', value: totals.total },
    { label: 'Approved', value: totals.approved },
    { label: 'Needs review', value: totals.needsReview },
    { label: 'Pending', value: totals.pending },
    { label: 'Rejected', value: totals.rejected },
  ];
}

function RecoveryFunnel({
  title,
  tone,
  rows,
}: {
  title: string;
  tone: 'emerald' | 'amber';
  rows: { label: string; value: string | number }[];
}) {
  const accent = tone === 'emerald' ? 'text-emerald-700' : 'text-amber-700';
  return (
    <div className="grid gap-0.5">
      <p className={cn('text-xs font-semibold uppercase tracking-wide', accent)}>{title}</p>
      <dl className="grid grid-cols-[auto_auto] gap-x-4">
        {rows.map((row, i) => (
          <div key={row.label} className="contents">
            <dt className="text-xs text-brand-slate">{row.label}</dt>
            <dd
              className={cn(
                'text-right font-mono tabular-nums',
                i === 0 ? cn('text-sm', accent) : 'text-xs text-brand-slate',
              )}
            >
              {formatCurrency(row.value)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function StatusBadge({
  status,
  pendingFindings,
}: {
  status: AuditRunOut['status'];
  pendingFindings: number;
}) {
  if (status === 'completed') {
    if (pendingFindings > 0) {
      return (
        <Badge className="border-transparent bg-amber-500 text-white hover:bg-amber-500/90">
          in review
        </Badge>
      );
    }
    return (
      <Badge className="border-transparent bg-emerald-600 text-white hover:bg-emerald-600/90">
        completed
      </Badge>
    );
  }
  const variant = status === 'failed' ? 'destructive' : 'secondary';
  return <Badge variant={variant}>{status}</Badge>;
}

/** Client-facing high-level PDF — the one-pager Andrew hands to the
 *  customer. The server renders the PDF in-process via reportlab +
 *  matplotlib and filters to approved findings only. */
function DownloadPdfReportButton({ runId }: { runId: string }) {
  const { getIdToken } = useAuth();
  const download = useMutation({
    mutationFn: async () => downloadRunReportPdf(runId, (await getIdToken()) ?? '', {}),
  });
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        onClick={() => download.mutate()}
        disabled={download.isPending}
      >
        {download.isPending ? 'Preparing…' : 'Download report (.pdf)'}
      </Button>
      {download.isError && (
        <span className="text-xs text-destructive">Download failed — see console.</span>
      )}
    </div>
  );
}

function DownloadRecoveryCsvButton({ runId }: { runId: string }) {
  const { getIdToken } = useAuth();
  const download = useMutation({
    mutationFn: async () => downloadRunReportCsv(runId, (await getIdToken()) ?? ''),
  });
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => download.mutate()}
        disabled={download.isPending}
      >
        {download.isPending ? 'Preparing…' : 'Download report (.csv)'}
      </Button>
      {download.isError && (
        <span className="text-xs text-destructive">Download failed — see console.</span>
      )}
    </div>
  );
}

function DownloadFindingsButton({ runId }: { runId: string }) {
  const { getIdToken } = useAuth();
  const download = useMutation({
    mutationFn: async () => downloadRunFindingsCsv(runId, (await getIdToken() ?? '')),
  });
  const errorMessage =
    download.error instanceof ApiError && download.error.status === 404
      ? "Findings aren't available for this run."
      : 'Download failed — see console.';
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => download.mutate()}
        disabled={download.isPending}
      >
        {download.isPending ? 'Preparing…' : 'Export findings (.csv)'}
      </Button>
      {download.isError && (
        <span className="text-xs text-destructive">{errorMessage}</span>
      )}
    </div>
  );
}

function DownloadNormalizedButton({ runId }: { runId: string }) {
  const { getIdToken } = useAuth();
  const download = useMutation({
    mutationFn: async () => downloadRunNormalizedCsv(runId, (await getIdToken() ?? '')),
  });
  const errorMessage =
    download.error instanceof ApiError && download.error.status === 404
      ? "Normalized CSV isn't available yet — try after the run completes."
      : 'Download failed — see console.';
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => download.mutate()}
        disabled={download.isPending}
      >
        {download.isPending ? 'Preparing…' : 'Download normalized (.csv)'}
      </Button>
      {download.isError && (
        <span className="text-xs text-destructive">{errorMessage}</span>
      )}
    </div>
  );
}

function DeleteRunButton({ run }: { run: AuditRunOut }) {
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  const del = useMutation({
    mutationFn: async () => deleteAuditRun(run.id, (await getIdToken() ?? '')),
    onSuccess: () => {
      // Invalidate the parent engagement's run list so the deleted row
      // disappears when the user navigates back, then route there.
      if (run.engagement_id) {
        qc.invalidateQueries({ queryKey: ['runs', 'by-engagement', run.engagement_id] });
        void navigate({ to: '/engagements/$id', params: { id: run.engagement_id } });
      } else {
        void navigate({ to: '/customers' });
      }
    },
  });

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setConfirming(true)}
      >
        Delete
      </Button>
      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogHeader>
          <DialogTitle>Delete this run?</DialogTitle>
          <DialogDescription>
            This permanently removes the run and every finding it produced.
            Source files uploaded for the run are preserved in S3 but the
            review history (approvals, rejections, notes) is gone.{' '}
            <span className="font-medium text-destructive">
              This cannot be undone.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {del.isError && (
            <span className="mr-auto text-xs text-destructive">
              delete failed — see console
            </span>
          )}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={del.isPending}
            onClick={() => setConfirming(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={del.isPending}
            onClick={() => del.mutate()}
          >
            {del.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
}
